import { NewButton } from '@/components/basic';
import { EditResourceForm } from '@/components/partial/ResourceSettingsPanel/ResourceSettingsPanel';
import LoadingSpinner from '@/components/util/LoadingSpinner';
import { useResourceSettingsState } from '@/hooks/useResourceSettings';
import { classNames } from '@/lib/classNames';
import { isTacticStaff } from '@/lib/featureFlags';
import { useAuthenticated } from '@gettactic/hooks';
import { DownloadIcon, MapIcon } from '@heroicons/react/outline';
import Link from 'next/link';
import React, { ChangeEventHandler } from 'react';
import { useDropzone } from 'react-dropzone';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { MdOutlineAddLocationAlt } from 'react-icons/md';

export interface MapPanelProps {
  loading: boolean;
  watch: UseFormWatch<EditResourceForm>;
  setValue: UseFormSetValue<EditResourceForm>;
}

export const MapPanel = ({ loading, watch, setValue }: MapPanelProps) => {
  const resourceContext = useResourceSettingsState();
  const resource = resourceContext.state.selected;
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const file = watch('map_file');
  const mapUrl = watch('map_url');
  const setFile = (newFile: File | null) => {
    setValue('map_file', newFile);
  };
  const setMapUrl = (newUrl: string | null) => {
    setValue('map_url', newUrl);
  };

  const handleFileChange: ChangeEventHandler<HTMLInputElement> | undefined = (
    ev
  ) => {
    if (!ev.currentTarget.files || !ev.currentTarget.files.length) {
      setFile(null);
    } else {
      const newFile: File = ev.currentTarget.files[0];
      setFile(newFile);
    }
  };

  function getMapNameFromUrl(url: string) {
    const u = new URL(url);
    const last = u.pathname.split('/').pop();
    const filename = decodeURIComponent(last || '');
    if (!filename) {
      return 'unknown';
    }
    return filename;
  }

  const RemoveIcon = (
    <svg
      className="h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: async ([file]) => {
      if (file) {
        setFile(file);
      } else {
        setFile(null);
      }
    },
    multiple: false,
    maxFiles: 1,
    accept: 'image/svg+xml'
  });

  return resource ? (
    <div>
      <div className="mt-5">
        <div className="flex items-center">
          <div
            {...getRootProps()}
            className={classNames(
              'cursor-pointer w-full px-6 pt-8 pb-6 flex flex-col flex-nowrap \
              justify-center items-center gap-4 border border-highlight/20 \
              border-dashed rounded-lg',
              isDragActive ? 'opacity-75' : ''
            )}
          >
            <input
              {...getInputProps()}
              onChange={handleFileChange}
              className="hidden"
              type="file"
              accept="image/svg+xml"
            />
            <div className="flex flex-col flex-nowrap gap-1 justify-center items-center text-tertiary">
              {isDragActive ? (
                <DownloadIcon className="w-14 h-14" />
              ) : file || mapUrl ? (
                <MapIcon className="w-14 h-14" />
              ) : (
                <MdOutlineAddLocationAlt className="w-14 h-14" />
              )}

              {file ? (
                <div className="overflow-hidden overflow-ellipsis flex items-center gap-2 text-base text-secondary [.tactic-v2_&]:text-secondary-foreground font-bold">
                  Map file:&nbsp;
                  <span className="overflow-hidden overflow-ellipsis break-all font-medium">
                    {file.name}
                  </span>
                  <button
                    type="button"
                    className="hover:text-tertiary"
                    onClick={(event) => {
                      if (loading) {
                        return;
                      }
                      setFile(null);
                      event.stopPropagation();
                    }}
                  >
                    {RemoveIcon}
                  </button>
                </div>
              ) : mapUrl ? (
                <div className="flex items-center gap-2 text-base text-secondary [.tactic-v2_&]:text-secondary-foreground">
                  <span className="font-medium overflow-hidden overflow-ellipsis">
                    Map SVG:&nbsp;
                    <a
                      rel="noreferrer noopener"
                      className="max-h-12 overflow-hidden overflow-ellipsis break-all font-bold"
                      href={mapUrl}
                      target="_blank"
                    >
                      {' '}
                      {getMapNameFromUrl(mapUrl)}
                    </a>
                  </span>
                  <button
                    type="button"
                    className="hover:text-tertiary"
                    onClick={(event) => {
                      if (loading) {
                        return;
                      }
                      setMapUrl(null);
                      event.stopPropagation();
                    }}
                  >
                    {RemoveIcon}
                  </button>
                </div>
              ) : (
                <div className="text-base text-secondary [.tactic-v2_&]:text-secondary-foreground font-bold">
                  {isDragActive ? <>Drop here</> : <>Upload or drop a SVG</>}
                </div>
              )}
            </div>
            <NewButton.Component
              variant={NewButton.Variant.PrimarySquared}
              theme={NewButton.Theme.Primary}
              size={NewButton.Size.Medium}
              type="button"
              disabled={loading}
            >
              {loading ? (
                <LoadingSpinner color="text-white" />
              ) : isDragActive ? (
                <>Drop Here</>
              ) : file ? (
                <>Change File</>
              ) : (
                <>Choose File</>
              )}
            </NewButton.Component>
          </div>
        </div>
      </div>
    </div>
  ) : null;
};
